import { createSelector } from 'reselect';
import createPerSliceIdSelector from '../helpers/createPerSliceIdSelector';
import get from '../helpers/get';
import transformLinkProperties from '../server/CMS/transformLinkProperties';

export const isBusy = state => !!state.content.busy;
export const getContentId = state => state.content.uid;
export const getCurrentContentId = state => get(state, ['content', 'content', 'id']);
export const getContent = state => state.content.content;
export const getContentError = state => state.content.error;
export const isContentReadyOrError = state => !isBusy(state)
  && !!(getContent(state) || getContentError(state));
export const getSlices = state => get(getContent(state), ['body']) || [];
export const getSlicesBySliceId = createSelector(
  getSlices,
  slices => slices.reduce((result, slice) => ({
    ...result,
    [slice.sliceId]: slice,
  }), {}),
);
export const getSliceContent = (state, sliceId) => getSlicesBySliceId(state)[sliceId];
export const getSliceContentType = (state, sliceId) => getSlicesBySliceId(state)[sliceId].type;
export const getSortedSlices = createSelector(
  getSlices,
  slices => {
    // keep existing array order except that seo items come last
    const seo = [];
    const others = [];
    slices.forEach(slice => {
      if (slice.type === 'seo') {
        seo.push(slice);
      } else {
        others.push(slice);
      }
    });
    return [...others, ...seo];
  },
);
export const getPreviewToken = state => state.content.previewToken;
export const getTitleAndTag = (content, fieldName, defaultTag = 'h2') => ({
  title: get(content, [fieldName, 'text']),
  tag: get(content, ['sliceLabel']) === `seo_h1_${fieldName}` ? 'h1' : defaultTag,
});

export const getSliceClickthrough = createPerSliceIdSelector(
  getSliceContent,
  content => content && transformLinkProperties(content, 'clickthrough'),
);
export const getSliceTitleAndTag = createPerSliceIdSelector(
  getSliceContent,
  content => {
    const { title, tag } = getTitleAndTag(content, 'title');
    return { title, titleTag: tag };
  },
);
export const getSliceCopy = (state, sliceId) => get(getSliceContent(state, sliceId), ['copy']);

export const getSliceItems = createPerSliceIdSelector(
  getSliceContent,
  content => get(content, ['items']) || [],
);
export const getSliceMobileSideScroller = createPerSliceIdSelector(
  getSliceContent,
  getSliceItems,
  (content, items) => items.filter(card => card.image_portrait).length > 1
    && get(content, ['sideways_scroll']) !== 'No',
);
export const getSliceAutoplayMilliseconds = createPerSliceIdSelector(
  getSliceContent,
  content => {
    const setting = get(content, ['autoplay']);
    if (setting) {
      const match = /^(\d+) *(sec(?:ond)?s?)?$/i.exec(setting);
      if (match) {
        return match[1] * 1000;
      }
    }
    return false;
  },
);
export const getSliceTypography = createPerSliceIdSelector(
  getSliceContent,
  content => get(content, ['typography']),
);

export const getSliceRichTextGridSize = (state, sliceId) => get(getSliceContent(state, sliceId), ['grid_size']);
export const getSliceNoBottomMargin = (state, sliceId) => get(getSliceContent(state, sliceId), ['bottom_margin']) === 'No';
export const getSliceBottomMarginSize = (state, sliceId) => get(getSliceContent(state, sliceId), ['bottom_margin_size']);
export const getSliceRichTextColumns = (state, sliceId) => get(getSliceContent(state, sliceId), ['columns']);
export const getSliceRichTextTitle = (state, sliceId) => get(getSliceContent(state, sliceId), ['title', 'html']);
export const getSliceTitleAlignment = (state, sliceId) => get(getSliceContent(state, sliceId), ['title_alignment']);
export const getSliceTextAlignment = (state, sliceId) => get(getSliceContent(state, sliceId), ['alignment']);
export const getSliceRichText = (state, sliceId) => get(getSliceContent(state, sliceId), ['text', 'html']);
export const getSliceCta = (state, sliceId) => get(getSliceContent(state, sliceId), ['cta']);
export const getSliceCtaAlignment = (state, sliceId) => get(getSliceContent(state, sliceId), ['cta_alignment']);
export const getSliceStretch = (state, sliceId) => get(getSliceContent(state, sliceId), ['stretch']);
export const getSliceAnchorId = (state, sliceId) => get(getSliceContent(state, sliceId), ['id']);
export const getSliceBackgroundImage = (state, sliceId) => get(getSliceContent(state, sliceId), ['background_image']);
export const getSliceBackgroundImageWidth = (state, sliceId) => get(getSliceContent(state, sliceId), ['background_image_width']);
export const getSliceColourTheme = (state, sliceId) => get(getSliceContent(state, sliceId), ['colour_theme']);
export const getSliceFontStyle = (state, sliceId) => get(getSliceContent(state, sliceId), ['font_style']);
export const getSliceTitleFontStyle = (state, sliceId) => get(getSliceContent(state, sliceId), ['title_font_style']);
export const getSliceHidden = (state, sliceId) => get(getSliceContent(state, sliceId), ['hidden']);
export const getSliceFaqStructuredDataEnabled = (state, sliceId) => !get(getSliceContent(state, sliceId), ['disable_structured_data']);

export const getMetadata = createSelector(
  getSlices,
  slices => slices.find(slice => slice.type === 'seo_metadata'),
);
